import React from 'react';

import {
  Close,
} from '@mui/icons-material';
import {
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import Button from '../../../components/Button';
import ContentCard from '../../../components/ContentCard';
import ProfileIcon from '../../../components/ProfileIcon';
import StudentStateChip from '../../../components/StudentStateChip';
import UserDisabledStateChip from '../../../components/UserDisabledStateChip';
import { useGetCourseQuery } from '../../../services/courses';
import { ShortStudent } from '../../../services/students/types';
import { CourseEnrolmentState } from '../../../types/common';
import { joinNames } from '../../../utils/common/formatText';

interface CourseAbsenceRowProps {
  courseId: string;
  absenceCount?: number;
}

const CourseAbsenceRow = ({
  courseId, absenceCount,
}: CourseAbsenceRowProps) => {
  const { data: course } = useGetCourseQuery({ courseId });

  if (!course || !absenceCount || absenceCount === 0) return null;

  return (
    <Stack key={courseId} direction="row" justifyContent="space-between">
      <Typography variant="body1">{course.data.label}</Typography>
      <Typography variant="body1">{absenceCount}</Typography>
    </Stack>
  );
};

interface StudentDetailsProps {
  student: ShortStudent;
  onClose: () => void;
}

const StudentDetails = ({
  student,
  onClose = () => {},
}: StudentDetailsProps) => {
  const theme = useTheme();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
          <Typography variant="h5">
            Student Details
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <ContentCard>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <ProfileIcon
                  size={72}
                  firstName={student.firstName}
                  lastName={student.lastName}
                  imageUrl={student.profileIcon?.image}
                  color={student.profileIcon?.color}
                />
                <Stack
                  direction="column"
                >
                  <Typography variant="h4" color={theme.palette.darkPrimary.main}>
                    {student.firstName}
                  </Typography>
                  <Typography variant="h4" color={theme.palette.darkPrimary.main}>
                    {student.lastName}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                {joinNames([student.gradYear, student?.school])} Cohort
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <StudentStateChip state={student.state} />
                <UserDisabledStateChip disabled={student.disabled} />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Button href={`/admin/student/${student.id}`} fullWidth>
                View Details
              </Button>
            </Grid>
          </Grid>
        </ContentCard>
      </Grid>
      <Grid item xs={12}>
        <ContentCard variant="outlined">
          <Stack direction="column" spacing={1}>
            <Typography variant="subtitle2">
              Email
            </Typography>
            <Typography variant="body1">
              {student.email}
            </Typography>
            <Typography variant="subtitle2">
              Parent Email
            </Typography>
            <Typography variant="body1">
              {student.parentEmail || 'N/A'}
            </Typography>
            <Typography variant="subtitle2">
              Phone Number
            </Typography>
            <Typography variant="body1">
              {student.phoneNumber}
            </Typography>
            <Typography variant="subtitle2">
              Parent Phone Number
            </Typography>
            <Typography variant="body1">
              {student.parentPhoneNumber || 'N/A'}
            </Typography>
          </Stack>
        </ContentCard>
      </Grid>
      {!!student.mostConsecutiveAbsences && student.courseStates && (
        <Grid item xs={12}>
          <ContentCard variant="outlined">
            <Stack direction="column" spacing={1}>
              <Typography variant="subtitle2">Absence Streaks</Typography>
              {Object.entries(student.courseStates)
                .filter(([, value]) => value.state !== CourseEnrolmentState.Unenrolled)
                .map(([key, value]) => {
                  const { consecutiveAbsences } = value;
                  const courseId = key.split('#')[1];
                  return (
                    <CourseAbsenceRow
                      key={courseId}
                      courseId={courseId}
                      absenceCount={consecutiveAbsences}
                    />
                  );
                })}
            </Stack>
          </ContentCard>
        </Grid>
      )}
    </Grid>
  );
};

export default StudentDetails;
